import { OtaTaskProps } from '@ixicare/resources';
import { formatDateTime } from '@ixicare/ui';
import { t } from '~i18n';

export type OtaTaskState =
  | 'scheduled'
  | 'waitingForDevice'
  | 'delivered'
  | 'waitingForDownload'
  | 'downloading'
  | 'waitingForInstall'
  | 'installing'
  | 'done'
  | 'failed';

/**
 * Where an update stands, as far as the backend can tell.
 *
 * A device fetches its update notification on check-in; once it acknowledged it, the notification
 * is gone ("delivered"). The status only moves when the device itself reports progress, so an
 * update that stays delivered without downloading has stalled and needs a retry. A watch keeps a
 * downloaded update until it is docked, so "waiting for install" can last days.
 */
export const otaTaskState = (task: OtaTaskProps, now: Date = new Date()): OtaTaskState => {
  switch (task.status) {
    case 'DONE':
      return 'done';
    case 'ERROR':
      return 'failed';
    case 'WAITING_FOR_DOWNLOAD':
      return 'waitingForDownload';
    case 'DOWNLOADING':
      return 'downloading';
    case 'WAITING_FOR_INSTALL':
      return 'waitingForInstall';
    case 'INSTALLING':
      return 'installing';
    case 'PLANNED':
      if (task.delivered) return 'delivered';
      return task.executionDatetime && task.executionDatetime > now ? 'scheduled' : 'waitingForDevice';
  }
};

export const canRetryOtaTask = (task: OtaTaskProps) => task.status !== 'DONE' && task.delivered;

export const otaTaskStateLabel = (task: OtaTaskProps): string => {
  const version = task.firmware?.version ?? '';
  switch (otaTaskState(task)) {
    case 'scheduled':
      return t('firmware.task.state.scheduled', {
        version,
        date: task.executionDatetime ? formatDateTime(task.executionDatetime) : '',
      });
    case 'waitingForDevice':
      return t('firmware.task.state.waitingForDevice', { version });
    case 'delivered':
      return t('firmware.task.state.delivered', { version });
    case 'waitingForDownload':
      return t('firmware.task.state.waitingForDownload', { version });
    case 'downloading':
      return t('firmware.task.state.downloading', { version });
    case 'waitingForInstall':
      return t('firmware.task.state.waitingForInstall', { version });
    case 'installing':
      return t('firmware.task.state.installing', { version });
    case 'done':
      return t('firmware.task.state.done', { version });
    case 'failed':
      return t('firmware.task.state.failed', { version });
  }
};
